import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {ContentDiv, HeadDiv, Span, TitleButton} from '../../../common/components/StyledComp';
import PaymentMethodManagement from '../../stripe/components/PaymentMethodManagement';
import {faReceipt} from '@fortawesome/free-solid-svg-icons';
import SubscriptionManagement from '../../stripe/components/SubscriptionManagement';
import DatatableLoadMore from '../../../common/components/Datatable/DatatableLoadMore';
import gql from 'graphql-tag';
import DisplayAmount from '../../../common/components/DisplayAmount';
import {dateFormatFR} from '../../../utils';
import DisplayInvoiceStatus from './DisplayInvoiceStatus';
import {INVOICE_STATUS} from '../../../common/constants';
import FlexRow from '../../../common/components/layout/FlexRow';
import DownloadInvoice from './DownloadInvoice';
import {withLoadedUser} from '../../../login/containers/AuthBase';
import HostedInvoiceUrl from './HostedStripeInvoice';
import PopIn from '../../../common/components/PopIn';
import {withRouter} from 'react-router';

const GRAPHQL_QUERY_CURRENT_USER_INVOICES = gql`
  query currentUserInvoices($criteria:CriteriaListInput, $additionalCriteria:InvoicesCriteriaListInput) {
    currentUser {
      id
      invoices(criteria: $criteria, additionalCriteria: $additionalCriteria){
        rows{
          id
          amountDue
          amountPaid
          amountRemaining
          created
          currency
          status
          invoicePdf
          hostedInvoiceUrl
          paid
          total
          tax
        }
        id
        hasMore
      }
    }
  }
`;

const columns = t => [
  {
    Header: t('created'),
    accessor: 'created',
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
  },
  {
    Header: t('status.title'),
    accessor: 'status',
    Cell: ({cell}) => <DisplayInvoiceStatus status={cell.value} />,
  },
  {
    Header: t('amountDue'),
    accessor: 'total',
    Cell: ({cell}) => <DisplayAmount amount={cell.value}/>,
  },
  {
    Header: t('tax'),
    accessor: 'tax',
    Cell: ({cell}) => <DisplayAmount amount={cell.value}/>,
  },
  {
    Header: t('bill'),
    Cell: ({cell}) => (
      <FlexRow>
        {cell.value.status !== INVOICE_STATUS.DRAFT && (
          <>
            <DownloadInvoice invoicePdf={cell.value.invoicePdf}/>
            <HostedInvoiceUrl hostedInvoiceUrl={cell.value.hostedInvoiceUrl}/>
          </>
        )}
      </FlexRow>
    ),
    accessor: row => row,
  },
];

const UserBillingDetails = ({t, currentUser, location}) => {
  const searchParams = new URLSearchParams(location.search);
  const [displayBecomePremium, setDisplayBecomePremium] = useState(!currentUser.hasActiveSubscription && !searchParams.has('newSubscription'));
  const [displaySubscriptionModal, setDisplaySubscriptionModal] = useState(false);

  return (
    <Screen
      header={
        <Metadata meta={t('premiumSubscription')} title={t('premiumSubscription')}/>
      }
      leftTitle={
        <>
          <FontAwesomeIcon icon={faReceipt} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('premiumSubscription')}</Span>
        </>
      }>
      <>
        <Container className="general-container">
          <Row className="info-row">
            <Col lg={12} xl={6}>
              <div className="section-info-user">
                <HeadDiv className="info-header-user">{`${t('subscription')} : `}</HeadDiv>
                <ContentDiv>
                  <SubscriptionManagement
                    key={displaySubscriptionModal}
                    userId={currentUser.id}
                    allowCancelSubscription={true}
                    displaySubscriptionModal={displaySubscriptionModal}
                  />
                </ContentDiv>
              </div>
            </Col>
            <Col lg={12} className={'d-xl-none'}>
              <br/>
            </Col>
            <Col lg={12} xl={6}>
              <div className="section-info-user">
                <HeadDiv className="info-header-user">{`${t('payment')} : `}</HeadDiv>
                <ContentDiv>
                  <PaymentMethodManagement userId={currentUser.id}/>
                </ContentDiv>
              </div>
            </Col>
          </Row>
        </Container>

        <DatatableLoadMore
          query={GRAPHQL_QUERY_CURRENT_USER_INVOICES}
          keyResult="currentUser.invoices"
          columns={columns(t)}
          fetchPolicy={'cache-and-network'}
        />

        <PopIn title={t('premiumSubscription')} visible={displayBecomePremium} setModalVisible={setDisplayBecomePremium} size={'m'}>
          <p>{t('premiumSubscriptionMessage')}</p>
          <div className="popin-buttons">
            <TitleButton onClick={() => {
              setDisplayBecomePremium(false);
              setDisplaySubscriptionModal(true);
            }}>{t('becomePremiumBtn')}</TitleButton>
          </div>
        </PopIn>
      </>
    </Screen>
  );
};

UserBillingDetails.propTypes = {
  t: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    hasActiveSubscription: PropTypes.bool.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default compose(
  withTranslation('billing'),
  withLoadedUser,
  withRouter,
)(UserBillingDetails);
